import { useRef, useState, useEffect } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useCart } from "@/contexts/CartContext";
import { useFlyToCart } from "@/hooks/useFlyToCart";
import { ShoppingCart, Check } from "lucide-react";
import { toast } from "sonner";
import { ProductApi } from "@/api/api";
import { buildImageUrl } from "@/lib/image";

interface QuickViewDialogProduct {
  id: number;
  image: string;
  title: string;
  category?: string;
  category_label?: string;
  description?: string;
}

interface ProductQuantity {
  id: number;
  product_id: number;
  price: number | string;
  quantities?: {
    id: number;
    name: string;
  };
}

interface ProductDetail {
  id: number;
  name: string;
  short_description?: string;
  detailed_description?: string;
  image_link?: string;
  product_quantities?: ProductQuantity[];
}

interface QuickViewDialogProps {
  product: QuickViewDialogProduct | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const formatPrice = (p: number) =>
  new Intl.NumberFormat("vi-VN").format(p) + "đ";

const QuickViewDialog = ({ product, open, onOpenChange }: QuickViewDialogProps) => {
  const { addToCart } = useCart();
  const { flyToCart } = useFlyToCart();
  const imgRef = useRef<HTMLImageElement>(null);
  const [productDetail, setProductDetail] = useState<ProductDetail | null>(null);
  const [selectedQuantityId, setSelectedQuantityId] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (product && open) {
      fetchProductDetail(product.id);
    }
  }, [product, open]);

  const fetchProductDetail = async (productId: number) => {
    try {
      setIsLoading(true);
      const res = await ProductApi.getById(productId);
      const detail = res.data;
      setProductDetail(detail);
      if (detail.product_quantities && detail.product_quantities.length > 0) {
        setSelectedQuantityId(detail.product_quantities[0].id);
      }
    } catch (error) {
      console.error("Lỗi khi fetch product detail:", error);
      toast.error("Không thể tải thông tin sản phẩm");
    } finally {
      setIsLoading(false);
    }
  };

  if (!product) return null;

  const currentQuantity = productDetail?.product_quantities?.find(
    (pq) => pq.id === selectedQuantityId
  );
  const currentPrice = currentQuantity ? Number(currentQuantity.price) : 0;
  const currentUnit = currentQuantity?.quantities?.name || "Sản phẩm";

  const handleAddToCart = () => {
    if (!currentQuantity) {
      toast.error("Vui lòng chọn loại sản phẩm");
      return;
    }

    addToCart(
      {
        id: product.id,
        image: product.image,
        title: product.title,
        price: currentPrice,
        unit: currentUnit,
      },
      1,
      currentQuantity.id
    );
    toast.success(`Đã thêm "${product.title}" vào giỏ hàng`);
    if (imgRef.current) flyToCart(product.image, imgRef.current);
    setTimeout(() => onOpenChange(false), 400);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] sm:max-w-[800px] p-0 overflow-hidden max-h-[90vh] overflow-y-auto">
        <DialogTitle className="sr-only">{product.title}</DialogTitle>
        <div className="grid grid-cols-1 sm:grid-cols-2">
          {/* Image */}
          <div className="rounded-lg overflow-hidden bg-muted/20 flex items-center justify-center">
            <img
                ref={imgRef}
                src={product.image}
                alt={product.title}
                className="max-w-full rounded-lg max-h-full"
            />
          </div>

          {/* Details */}
          <div className="p-6 flex flex-col justify-between">
            <div>
              {/* Category */}
              <span className="text-xs text-muted-foreground tracking-wider">
                {product.category_label}
              </span>

              {/* Title */}
              <h3 className="text-3xl font-heading uppercase font-bold text-foreground mt-1 mb-8">
                {product.title}
              </h3>

              {/* Variant Selector as Pills */}
              {productDetail?.product_quantities && productDetail.product_quantities.length > 0 && (
                <div className="mb-5 mt-8">
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Loại sản phẩm
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {productDetail.product_quantities.map((pq) => (
                      <button
                        key={pq.id}
                        onClick={() => setSelectedQuantityId(pq.id)}
                        className={`px-3 py-1.5 text-xs font-medium rounded-sm border transition-colors ${
                          selectedQuantityId === pq.id
                            ? "border-primary-cyan bg-primary-cyan/10 text-primary-cyan"
                            : "border-border text-muted-foreground hover:border-foreground"
                        }`}
                      >
                        {pq.quantities?.name} - {formatPrice(Number(pq.price))}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Đặc điểm nổi bật - Key Features */}
              {productDetail?.detailed_description && (
                <div className="mb-4">
                  <p className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wider">
                    Đặc điểm nổi bật
                  </p>
                  <div className="text-sm text-foreground/80 leading-relaxed space-y-1.5">
                    {productDetail.detailed_description
                      .split('\n')
                      .filter(line => line.trim() !== '')
                      .map((line, index) => (
                        <div key={index} className="flex items-start gap-2">
                          <Check size={14} className="text-primary-cyan mt-0.5 shrink-0" />
                          <span>{line.trim()}</span>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>

            {/* Add to Cart Button */}
            <button
              onClick={handleAddToCart}
              disabled={isLoading}
              className="mt-6 w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground py-3 text-sm tracking-wider hover:bg-primary-cyan transition-colors uppercase disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ShoppingCart size={16} />
              {isLoading ? "Đang tải..." : "Thêm vào giỏ hàng"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default QuickViewDialog;
